import dayjs from 'dayjs';
import { DATE_TIME_FORMAT_SECONDS } from 'utils/constants/dateTime';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { truncateTo2dp } from 'utils/helpers/general';
import { getLabelWrtUnitSystem } from 'utils/helpers/unitSystem';
import i18n from 'i18n';

const formatDuration = (durationMs: number): string => {
  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}${
      minutes > 0 ? ` and ${minutes} minute${minutes > 1 ? 's' : ''}` : ''
    }`;
  }
  if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}${
      seconds > 0 ? ` and ${seconds} second${seconds > 1 ? 's' : ''}` : ''
    }`;
  }
  return `${seconds} second${seconds > 1 ? 's' : ''}`;
};

export const AssetWiseTimeLineChartTooltip = ({
  seriesIndex,
  dataPointIndex,
  w,
  unitSystem,
  isMarginTop = false,
  preferredTimeDisplayMode,
  timezone,
}: any): string => {
  const seriesName = w.globals.initialSeries[seriesIndex]?.name;
  const dataPoint = w.globals.initialSeries[seriesIndex].data[dataPointIndex];

  // Check if this is a custom loading/unloading period
  const displayEventName = dataPoint?.customEventType || seriesName;

  const startLoc = dataPoint?.extraDetails?.startLocation;
  const startLocation = /^[dD]/.test(startLoc)
    ? i18n.t('dumping')
    : `${i18n.t('tooltip_loading_site')} ${startLoc}`;

  const start = dataPoint?.extraDetails?.start;
  const end = dataPoint?.extraDetails?.end;

  const eventConfig = {
    isInstantEvent: false,
    tooltipConfig: {
      showGear: false,
      showDuration: false,
      showCurrentAndNextGear: false,
      showCurrentAndPreviousSpeed: false,
      showSpeed: false,
    },
  };
  const seriesColor = w.globals.colors[seriesIndex];
  const title = dataPoint.x; // Include the title
  const startTime = convertUtcToDisplayTime(
    start,
    preferredTimeDisplayMode,
    timezone,
    DATE_TIME_FORMAT_SECONDS
  );
  const endTime = convertUtcToDisplayTime(
    end,
    preferredTimeDisplayMode,
    timezone,
    DATE_TIME_FORMAT_SECONDS
  );

  const durationMs = dayjs(dataPoint.y[1]).diff(dayjs(dataPoint.y[0]));

  // Function to format the duration dynamically

  const formattedDuration = formatDuration(durationMs);

  return `
        <div style="
          background: #2E3B4E; 
          color: #FFFFFF; 
          padding: 12px; 
          border-radius: 8px; 
          font-family: Arial, sans-serif; 
          box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2); 
          min-width: 220px;
          ${isMarginTop ? 'margin-top: 80px;' : ''}
          ">
          <div style="font-weight: bold; font-size: 14px; margin-bottom: 8px; text-align: center; color: white;">
            ${title}
          </div>
          <div style="font-size: 12px; margin-bottom: 4px; text-transform: capitalize;">
            <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
    'tooltip_event'
  )}:</span> ${displayEventName} ${startLocation}
          </div>
          ${
            eventConfig?.isInstantEvent
              ? `
              <div style="font-size: 12px; margin-bottom: 4px;">
                <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_event_time'
                )}</span> ${startTime}
              </div>
              `
              : `
          <div style="font-size: 12px; margin-bottom: 4px;">
            <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_start'
                )}</span> ${startTime}
          </div>
          <div style="font-size: 12px; margin-bottom: 4px;">
            <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_end'
                )}</span> ${endTime}
          </div>
          `
          }
          ${
            eventConfig?.tooltipConfig?.showDuration
              ? `
            <div style="font-size: 12px; margin-bottom: 4px;">
              <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_duration'
                )}</span> ${formattedDuration}
            </div>
            `
              : ``
          }
          ${
            eventConfig?.tooltipConfig?.showCurrentAndNextGear
              ? `
                <div style="font-size: 12px; margin-bottom: 4px;">
                  <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_current_gear'
                )}</span> ${truncateTo2dp(dataPoint?.current_gear)}
                </div>
                <div style="font-size: 12px; margin-bottom: 4px;">
                  <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_next_gear'
                )}</span> ${truncateTo2dp(dataPoint?.next_gear)}
                </div>
              `
              : ``
          }
          ${
            eventConfig?.tooltipConfig?.showGear
              ? `
                <div style="font-size: 12px; margin-bottom: 4px;">
                  <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_gear'
                )}</span> ${truncateTo2dp(dataPoint?.gear_value)}
                </div>
              `
              : ``
          }
          ${
            eventConfig?.tooltipConfig?.showCurrentAndPreviousSpeed
              ? `
                <div style="font-size: 12px; margin-bottom: 4px;">
                  <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_previous_speed'
                )}</span>
                  ${truncateTo2dp(dataPoint?.previous_speed)}
                  ${getLabelWrtUnitSystem('KMH', unitSystem)}
                </div>
                <div style="font-size: 12px; margin-bottom: 4px;">
                  <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_current_speed'
                )}</span>
                  ${truncateTo2dp(dataPoint?.current_speed)}
                  ${getLabelWrtUnitSystem('KMH', unitSystem)}
                </div>
                <div style="font-size: 12px; margin-bottom: 4px;">
                  <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_difference'
                )}</span>
                  ${truncateTo2dp(
                    Number(dataPoint?.current_speed) -
                      Number(dataPoint?.previous_speed)
                  )}
                  ${getLabelWrtUnitSystem('KMH', unitSystem)}
                </div>
              `
              : ``
          }
          ${
            eventConfig?.tooltipConfig?.showSpeed
              ? `
                <div style="font-size: 12px; margin-bottom: 4px;">
                  <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_speed'
                )}:</span>
                  ${
                    dataPoint?.speed !== null && +dataPoint?.speed >= 0
                      ? truncateTo2dp(dataPoint?.speed)
                      : i18n.t('no_data')
                  }
                  ${
                    dataPoint?.speed !== null && +dataPoint?.speed >= 0
                      ? getLabelWrtUnitSystem('KMH', unitSystem)
                      : ''
                  }
                </div>
            `
              : ``
          }
          ${
            dataPoint?.avg_speed
              ? `
            <div style="font-size: 12px; margin-top: 8px; color: white;">
              <span style="color: ${seriesColor}; font-weight: bold;">${i18n.t(
                  'tooltip_average_speed'
                )}</span>
              ${truncateTo2dp(dataPoint?.avg_speed)}
              ${getLabelWrtUnitSystem('KMH', unitSystem)}
            </div>
            `
              : ``
          }
        </div>
      `;
};
